import React from 'react'
import styled from '@emotion/styled'
import { ThemeProvider } from '@emotion/react'
import { useEffect, useState } from 'react'
import { Link } from 'gatsby'
import _ from 'lodash'
import mq from '../styles/mediaQueries'

import Logo from '../components/Logo'
import MobileNavBar from './MobileNavBar'
import HamburgerSVG from '../assets/icons/hamburger.svg'
import ThumbsUpSVG from '../assets/icons/thumbs.svg'

const dayTheme = {
  fontColor: 'var(--terminal-500)',
  borderColor: 'var(--terminal-200)',
}

const nightTheme = {
  fontColor: 'var(--terminal-0)',
  borderColor: 'rgba(255,255,255,0.25)',
}

const blueTheme = {
  fontColor: 'var(--terminal-0)',
  borderColor: 'var(--terminal-0)',
}

const overlayTheme = {
  fontColor: 'var(--terminal-0)',
  borderColor: 'var(--screen-400)',
}

const Hamburger = props => {
  return <HamburgerSVG {...props} style={{ display: 'block' }} />
}

const ThumbsUp = props => {
  return <ThumbsUpSVG {...props} style={{ display: 'block' }} />
}

const NavBarHamburger = styled(Hamburger)`
  margin: 18px;
  height: 24px;
  width: 24px;

  path {
    stroke: ${props => props.theme.fontColor};
  }
`

const NavBarThumbsUp = styled(ThumbsUp)`
  margin-left: 9.3px;
  width: 24px;
  height: 24px;
  transform: rotate(15deg);

  path {
    stroke: ${props => props.theme.fontColor};
  }
`

const NavBarMainWrapper = styled.div`
  position: fixed;
  z-index: ${props =>
    props.invertedBelow ? 'unset' : props.invertedAbove ? '4' : '3'};
  ${mq({
    display: ['none', 'unset', 'unset', 'unset', 'unset'],
  })};
`

const NavBarWrapper = styled.div(props =>
  mq({
    position: ['fixed'],
    height: ['60px'],
    left: ['12px'],
    right: ['12px'],
    inset: ['12px 12px 0 12px'],
    borderLeft: [
      `${props.theme.borderColor} 1px solid`,
      `${props.theme.borderColor} 1px solid`,
      'transparent 0 solid',
      'transparent 0 solid',
      'transparent 0 solid',
    ],
    borderTop: ['1px solid'],
    borderRight: ['1px solid'],
    borderBottom: ['1px solid'],
    borderColor: [props.theme.borderColor],
    zIndex: ['10'],
  })
)

const NavBarList = styled.ul`
  display: flex;
  align-items: center;
  padding-inline-start: 0;
  margin-block-start: 0;
  margin-block-end: 0;
  ${mq({
    justifyContent: ['space-between', 'normal', 'normal', 'normal', 'normal'],
  })};
`

const NavBarListItem = styled.li`
  list-style: none;
`

const NavBarHamburgerListItem = styled.li`
  list-style: none;
  display: inline-block;
  position: relative;
  z-index: 1;
  height: 60px;
  width: 60px;
  pointer-events: auto;
  cursor: pointer;
`

const NavBarContact = styled(Link)`
  ${mq({
    display: ['none', 'flex', 'flex', 'flex', 'flex'],
  })};
  list-style: none;
  margin-left: auto;
  margin-right: 15.3px;
  align-items: center;
  flex-direction: row;
  text-decoration: none;

  font-family: degular, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 100%;
  color: ${props => props.theme.fontColor};

  &:hover {
    opacity: 0.5;
  }

  &:hover .thumb {
    transform: rotate(35deg);
  }
`

const NavBarLogo = styled(Logo)`
  width: 120px;
  height: 17.88px;

  path {
    fill: ${props => props.theme.fontColor};
  }

  ${mq({
    marginLeft: ['0', '21px', '24px', '24px', '24px'],
    marginRight: ['24px', '0', '0', '0', '0'],
  })};
`

const NavBarLeft = styled.div`
  ${mq({
    display: ['none', 'none', 'block', 'block', 'block'],
  })};
  position: fixed;
  width: 60px;
  z-index: 2;
  height: calc(100vh - 24px);
  border-top: none;
  border-right: 1px solid;
  border-bottom: 1px solid;
  border-left: 1px solid;
  border-color: ${props => props.theme.borderColor};
  inset: 12px 0 12px 12px;
`

const NavBarText = styled.div`
  ${mq({
    visibility: ['hidden', 'hidden', 'visible', 'visible', 'visible'],
  })};
  writing-mode: tb-rl;
  :first-of-type {
    margin-top: 84px;
  }
  margin: 24px 0 0 24px;

  font-family: var(--mono-font-stack);
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  color: ${props => props.theme.fontColor};
`

const NavBarBackground = styled.div`
  position: fixed;
  background-color: var(--screen-500);
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  opacity: ${props => (props.isOverlayOpen ? '1' : '0')};
  z-index: ${props => (props.isOverlayOpen ? '2' : '-10')};
  display: ${props => (props.isOverlayOpen ? 'block' : 'none')};
`

function NavBar({
  invertedAbove,
  invertedBelow,
  isOverlayOpen,
  setIsOverlayOpen,
  pixels,
  setPixels,
  theme,
  title,
}) {
  const pageTheme =
    theme === 'night'
      ? nightTheme
      : theme === 'day'
      ? dayTheme
      : theme === 'blue'
      ? blueTheme
      : null

  const [colorTheme, setColorTheme] = useState(pageTheme)

  function setScrollPixels() {
    window.onscroll = _.throttle(
      () => setPixels(Math.floor(window.pageYOffset)),
      100
    )
  }

  useEffect(() => {
    isOverlayOpen ? setColorTheme(overlayTheme) : setColorTheme(pageTheme)
  }, [isOverlayOpen, pageTheme])

  useEffect(() => {
    requestAnimationFrame(setScrollPixels)
  }, [])

  function toggleNav(event) {
    event.preventDefault()
    setIsOverlayOpen(!isOverlayOpen)
  }

  useEffect(() => {
    if (isOverlayOpen) {
      document.querySelector('body').style.cssText =
        'height: 100vh; overflow: hidden; color: var(--terminal-0);'
      document.body.style.cssText =
        'height: 100vh; overflow: hidden; color: var(--terminal-0);'
    }

    if (!isOverlayOpen) {
      document.querySelector('body').style.cssText =
        'height: unset; overflow: unset;'
      document.body.style.cssText = 'height: unset; overflow: unset;'
    }
  })

  return (
    <ThemeProvider theme={colorTheme}>
      <NavBarMainWrapper
        isOverlayOpen={isOverlayOpen}
        invertedAbove={invertedAbove}
        invertedBelow={invertedBelow}
      >
        <NavBarBackground isOverlayOpen={isOverlayOpen} />
        <NavBarWrapper>
          <NavBarList>
            <NavBarHamburgerListItem onClick={toggleNav}>
              <NavBarHamburger />
            </NavBarHamburgerListItem>
            <NavBarListItem>
              <Link to="/">
                <NavBarLogo alt="Logo" />
              </Link>
            </NavBarListItem>
            <NavBarContact to="/contact">
              Contact
              <NavBarThumbsUp className={'thumb'} />
            </NavBarContact>
          </NavBarList>
        </NavBarWrapper>
        <NavBarLeft>
          <NavBarText>{title}</NavBarText>
          <NavBarText>{pixels} pixels scrolled</NavBarText>
        </NavBarLeft>
      </NavBarMainWrapper>
      <MobileNavBar
        isOverlayOpen={isOverlayOpen}
        toggleNav={toggleNav}
        theme={theme}
        invertedAbove={invertedAbove}
      />
    </ThemeProvider>
  )
}

export default NavBar
